import { useEffect, useMemo, useState } from 'react';
import { bossDisplayName } from '../../../shared/domain/bossPresentation.js';
import { formatRaidHpEok } from '../../../shared/domain/raidHpFormat.js';
import { DororaidIcon } from '../../../shared/ui/DororaidIcon.jsx';
import { RaidPracticeDeckDisplay, RaidPracticeDeckPicker } from '../../raid-practice/shared/RaidPracticeDeckPicker.jsx';

function formatDamage(value) {
  if (value === null || value === undefined) return '-';
  return `${Number(value || 0).toLocaleString('ko-KR', { maximumFractionDigits: 3 })}억`;
}

const EMPTY_ARRAY = Object.freeze([]);

function asArray(value) {
  return Array.isArray(value) ? value : EMPTY_ARRAY;
}

function asValuesArray(value) {
  if (value && typeof value.values === 'function') return [...value.values()];
  return asArray(value);
}

function practiceProjection(boss, damageEok) {
  const damage = Math.max(Number(damageEok || 0), 0);
  if (!boss) return null;
  if (boss.isInfinite) return { infinite: true, damage, summary: `예상 ${formatDamage(damage)}` };
  const current = Math.max(Number(boss.remainingHpEok || 0), 0);
  const remaining = Math.max(current - damage, 0);
  const overkill = Math.max(damage - current, 0);
  return {
    infinite: false,
    current,
    remaining,
    overkill,
    kill: damage >= current && current > 0,
    summary: damage >= current && current > 0
      ? `처치 예상${overkill > 0 ? ` · 초과 ${formatDamage(overkill)}` : ''}`
      : `잔여 ${formatDamage(remaining)}`,
  };
}

export function PracticeProjection({ boss, damageEok, compact = false }) {
  const projection = practiceProjection(boss, damageEok);
  if (!projection) return null;
  if (compact) return <span className={projection.kill ? 'kill' : ''}>{projection.summary}</span>;
  if (projection.infinite) return <div className="raid-control-practice-projection"><span>인피니티 보스</span><strong>{projection.summary}</strong></div>;
  return <div className={`raid-control-practice-projection ${projection.kill ? 'kill' : ''}`}>
    <span>현재 {formatDamage(projection.current)} → 공격 후</span>
    <strong>{formatDamage(projection.remaining)}{projection.kill ? ' · 처치 예상' : ''}</strong>
    {projection.overkill > 0 ? <small>초과 피해 {formatDamage(projection.overkill)}</small> : null}
  </div>;
}

export function OrderDialog({ room, initialRecord, initialMode = 'saved', mobile = false, onClose, onDone }) {
  const bosses = room.bosses;
  const [mode, setMode] = useState(initialRecord ? 'saved' : initialMode);
  const [bossNo, setBossNo] = useState(initialRecord?.bossNo || room.selectedBossNo || 1);
  const [recordId, setRecordId] = useState(initialRecord?.id || '');
  const [customTitle, setCustomTitle] = useState('직접 작성 덱');
  const [customDamage, setCustomDamage] = useState('');
  const [customDeck, setCustomDeck] = useState([]);
  const [note, setNote] = useState('');
  const boss = bosses.find((item) => item.bossNo === Number(bossNo));
  const records = useMemo(() => room.selectedPracticeRecords.filter((record) => record.bossNo === Number(bossNo)), [bossNo, room.selectedPracticeRecords]);
  const selectedRecord = records.find((record) => record.id === recordId) || null;
  const customDamageValue = Number(customDamage || 0);
  const customDeckComplete = customDeck.filter(Boolean).length === 5;
  const customValid = customTitle.trim() && customDamageValue > 0 && customDeckComplete;
  const savedValid = Boolean(selectedRecord);

  useEffect(() => {
    if (mode !== 'saved') return;
    setRecordId((current) => records.some((record) => record.id === current)
      ? current
      : records[0]?.id || '');
  }, [mode, records]);

  const submit = async (event) => {
    event.preventDefault();
    if ((mode === 'saved' && !savedValid) || (mode === 'custom' && !customValid)) return;
    const ok = await room.createOrder({
      targetMemberOpenid: room.selectedMember.memberOpenid,
      bossNo,
      phaseNo: boss?.currentPhase || 1,
      practiceRecordId: mode === 'saved' ? selectedRecord?.id || null : null,
      title: mode === 'custom' ? customTitle.trim() : null,
      expectedDamageEok: mode === 'custom' ? customDamageValue : null,
      customDeck: mode === 'custom' ? customDeck : null,
      note,
    });
    if (ok) onDone();
  };
  return <div className="raid-control-dialog-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}><form className="raid-control-order-dialog" onSubmit={submit} role="dialog" aria-modal="true" aria-labelledby="raid-control-order-title">
    <header><div><h2 id="raid-control-order-title">{room.selectedMember?.nickname} 공격 지시</h2></div><button type="button" onClick={onClose} aria-label="닫기"><DororaidIcon name="close" size={20} /></button></header>
    <div className="raid-control-order-mode" role="tablist" aria-label="공격 지시 덱 선택 방식"><button type="button" role="tab" aria-selected={mode === 'saved'} className={mode === 'saved' ? 'active' : ''} onClick={() => setMode('saved')}>저장 슬롯</button><button type="button" role="tab" aria-selected={mode === 'custom'} className={mode === 'custom' ? 'active' : ''} onClick={() => setMode('custom')}>직접 작성</button></div>
    <label><span>보스</span><select value={bossNo} onChange={(event) => { setBossNo(Number(event.target.value)); setRecordId(''); }}>{bosses.map((item) => <option value={item.bossNo} key={item.bossNo}>{item.bossName} · {item.currentPhase}단</option>)}</select></label>
    {mode === 'saved' ? <div className="raid-control-saved-order-fields">
      <label><span>모의전 슬롯</span><select value={recordId} disabled={!records.length} onChange={(event) => setRecordId(event.target.value)}>{records.length ? records.map((record) => <option value={record.id} key={record.id}>슬롯 {record.slotNo} · {record.title || '모의전'} · {formatDamage(record.damageEok)}</option>) : <option value="">사용 가능한 슬롯 없음</option>}</select></label>
      {selectedRecord ? <div className="raid-control-saved-order-preview">
        <span className="raid-control-request-practice-summary"><small>슬롯 {selectedRecord.slotNo}</small><strong>{selectedRecord.title || '저장 모의전'}</strong><em>{formatDamage(selectedRecord.damageEok)}</em></span>
        <RaidPracticeDeckDisplay deck={selectedRecord.deck} mobile={mobile} />
        <PracticeProjection boss={boss} damageEok={selectedRecord.damageEok} />
      </div> : <div className="raid-control-request-practice-empty"><strong>이 보스에 사용할 수 있는 모의전 슬롯이 없습니다.</strong></div>}
    </div> : <div className="raid-control-custom-order-fields">
      <div className="raid-control-custom-order-primary"><label><span>덱 이름</span><input value={customTitle} maxLength={80} onChange={(event) => setCustomTitle(event.target.value)} placeholder="예: 흑련 1버 지연덱" /></label><label><span>예상 피해량(억)</span><input type="number" min="0.001" step="0.001" value={customDamage} onChange={(event) => setCustomDamage(event.target.value)} placeholder="예: 650" /></label></div>
      <div className="raid-control-custom-deck"><span>니케 선택 <b>{customDeck.filter(Boolean).length}/5</b></span><RaidPracticeDeckPicker key={room.selectedMember?.memberOpenid || 'self'} value={customDeck} onChange={setCustomDeck} memberOpenid={room.selectedMember?.memberOpenid || ''} mobile={mobile} /></div>
      {customDamageValue > 0 ? <PracticeProjection boss={boss} damageEok={customDamageValue} /> : null}
    </div>}
    <label><span>지시 메모</span><textarea value={note} maxLength={500} rows={3} placeholder="예: 1페 버스트 지연, 막타 대기" onChange={(event) => setNote(event.target.value)} /></label>
    <p>멤버 또는 관리자가 ‘공격 시작’을 누르면 관제 화면에 공격 중으로 표시합니다. 같은 보스를 다른 멤버가 공격 중이면 경고가 표시되며, ‘확인’을 누르면 함께 공격을 시작하고 ‘대기’를 누르면 현재 상태를 유지합니다.</p>
    <div><button type="button" onClick={onClose}>닫기</button><button className="primary" type="submit" disabled={room.working === 'order' || (mode === 'saved' && !savedValid) || (mode === 'custom' && !customValid)}>{room.working === 'order' ? '전송 중…' : '공격 지시 전송'}</button></div>
  </form></div>;
}
